import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { IoClose, IoChevronBack, IoChevronForward } from "react-icons/io5";
import ProjectPicture from "./ProjectPicture";
import { projectMeta, isInProgress } from "./projects";

const SWIPE_THRESHOLD = 50;

// Full-screen, keyboard- and touch-friendly photo viewer.
// Headless UI's Dialog provides the focus trap, Esc-to-close and scroll lock.
function ProjectLightbox({ project, onClose }) {
  const [index, setIndex] = useState(0);
  const total = project.images.length;
  const thumbRefs = useRef([]);
  const pointerStart = useRef(null);

  const go = useCallback((next) => setIndex((current) => (next(current) + total) % total), [total]);
  const prev = useCallback(() => go((i) => i - 1), [go]);
  const next = useCallback(() => go((i) => i + 1), [go]);

  useEffect(() => setIndex(0), [project]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "Home") setIndex(0);
      else if (e.key === "End") setIndex(total - 1);
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prev, next, total]);

  // Keep the active thumbnail visible in the strip.
  useEffect(() => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    thumbRefs.current[index]?.scrollIntoView({
      block: "nearest",
      inline: "center",
      behavior: reduce ? "auto" : "smooth",
    });
  }, [index]);

  const onPointerDown = (e) => {
    if (e.pointerType === "mouse") return;
    pointerStart.current = { x: e.clientX, y: e.clientY };
  };
  const onPointerUp = (e) => {
    const start = pointerStart.current;
    pointerStart.current = null;
    if (!start) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) next();
      else prev();
    }
  };

  const image = project.images[index];
  const neighbours = total > 1 ? [...new Set([(index + 1) % total, (index - 1 + total) % total])] : [];
  const meta = projectMeta(project);

  return (
    <Dialog open onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-[#0c0a1f]" aria-hidden="true" />
      <div className="fixed inset-0 flex">
        <DialogPanel className="flex h-full w-full flex-col text-white motion-safe:animate-[fadeIn_200ms_ease-out]">
          {/* Header */}
          <div className="flex items-start gap-4 px-4 sm:px-6 pt-4 pb-3">
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-base sm:text-xl font-bold leading-snug line-clamp-2">
                {project.title}
              </DialogTitle>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs sm:text-sm">
                <span className="rounded-full bg-brand-yellow px-3 py-0.5 font-semibold text-brand-navy">
                  {project.projectCategory}
                </span>
                {isInProgress(project) && (
                  <span className="rounded-full border border-brand-yellow px-3 py-0.5 font-semibold text-brand-yellow">
                    In Progress
                  </span>
                )}
                {meta.map(([label, value]) => (
                  <span key={label} className="rounded-full bg-white/10 px-3 py-0.5 text-gray-200">
                    <span className="text-gray-400">{label}:</span> {value}
                  </span>
                ))}
              </div>
              {project.description && (
                <p className="mt-2 max-w-3xl text-sm text-gray-300 line-clamp-3">{project.description}</p>
              )}
            </div>
            <p className="shrink-0 pt-1 text-sm font-medium tabular-nums text-gray-300" aria-live="polite">
              <span className="sr-only">Photo </span>
              {index + 1} / {total}
            </p>
            <button
              type="button"
              onClick={onClose}
              data-autofocus
              aria-label="Close gallery"
              className="shrink-0 rounded-full bg-white/10 p-2.5 text-2xl transition-colors hover:bg-white hover:text-brand-navy"
            >
              <IoClose />
            </button>
          </div>

          {/* Stage */}
          <div
            className="relative flex min-h-0 flex-1 items-center justify-center px-2 sm:px-20 touch-pan-y select-none"
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
            onPointerCancel={() => (pointerStart.current = null)}
          >
            <ProjectPicture
              key={image.file}
              project={project}
              image={image}
              alt={`${project.title}, photo ${index + 1} of ${total}`}
              sizes="(min-width: 900px) 900px, 100vw"
              loading="eager"
              draggable={false}
              className="max-h-full max-w-full w-auto h-auto object-contain rounded-lg shadow-2xl motion-safe:animate-[fadeIn_200ms_ease-out]"
            />

            {total > 1 && (
              <>
                <button
                  type="button"
                  onClick={prev}
                  aria-label="Previous photo"
                  className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-3 text-2xl text-brand-navy shadow-lg transition-colors hover:bg-brand-yellow"
                >
                  <IoChevronBack />
                </button>
                <button
                  type="button"
                  onClick={next}
                  aria-label="Next photo"
                  className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-3 text-2xl text-brand-navy shadow-lg transition-colors hover:bg-brand-yellow"
                >
                  <IoChevronForward />
                </button>
              </>
            )}

            {/* Warm the cache for the neighbouring photos */}
            <div className="hidden" aria-hidden="true">
              {neighbours.map((i) => (
                <ProjectPicture
                  key={project.images[i].file}
                  project={project}
                  image={project.images[i]}
                  alt=""
                  sizes="(min-width: 900px) 900px, 100vw"
                  loading="eager"
                />
              ))}
            </div>
          </div>

          {/* Thumbnails */}
          {total > 1 && (
            <div className="px-4 sm:px-6 py-3">
              <ul className="flex gap-2 overflow-x-auto pb-1" aria-label="Photos in this project">
                {project.images.map((img, i) => (
                  <li key={img.file} className="shrink-0">
                    <button
                      ref={(el) => (thumbRefs.current[i] = el)}
                      type="button"
                      onClick={() => setIndex(i)}
                      aria-label={`Show photo ${i + 1} of ${total}`}
                      aria-current={i === index ? "true" : undefined}
                      className={`block h-14 w-20 sm:h-16 sm:w-24 overflow-hidden rounded-md border-2 transition-all ${
                        i === index
                          ? "border-brand-yellow opacity-100"
                          : "border-transparent opacity-50 hover:opacity-100"
                      }`}
                    >
                      <ProjectPicture
                        project={project}
                        image={img}
                        alt=""
                        sizes="96px"
                        className="h-full w-full object-cover"
                      />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  );
}

export default ProjectLightbox;
